import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useListService from '@/data/hook/useListService';
import { lessonPlanItemQueries } from '@/data/query-keys/lessonPlanItemQueryKey';
import {
  CreateLessonPlanItemBody,
  createLessonPlanItem,
  removeLessonPlanItem,
  updateLessonPlanItem,
} from '@/data/services/lessonPlanItemServices';
import LessonPlanItem, { LessonPlanItemDTO } from '@/models/LessonPlanItem';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import { groupItems } from '@/utils/shared/groupItems';

type UseLessonItemsMapProps = {
  lessonPlanIds: number[];
};

type UpdateMutationArgs = {
  lessonPlanItemId: number;
  data: Partial<LessonPlanItemDTO>;
};

const useLessonItemsMap = ({ lessonPlanIds }: UseLessonItemsMapProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonPlansTab',
  });

  const {
    results: lessonPlanItems,
    isInitialLoading,
    cacheManager,
  } = useListService<LessonPlanItem>({
    ...lessonPlanItemQueries.list({
      lessonPlanId: lessonPlanIds,
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    enabled: lessonPlanIds.length > 0,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const itemsMap = groupItems(lessonPlanItems, item =>
    String(item.lessonPlanId),
  );

  const createMutation = useMutation({
    mutationFn: (body: CreateLessonPlanItemBody) => createLessonPlanItem(body),
    onSuccess: lessonPlanItem => {
      cacheManager.add(lessonPlanItem);
      alert.success(t('itemCreateSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ lessonPlanItemId, data }: UpdateMutationArgs) =>
      updateLessonPlanItem(lessonPlanItemId, data),
    onSuccess: lessonPlanItem => {
      cacheManager.update(lessonPlanItem);
      alert.success(t('itemUpdateSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (lessonPlanItemId: number) =>
      removeLessonPlanItem(lessonPlanItemId),
    onSuccess: (_, lessonPlanItemId) => {
      cacheManager.remove(lessonPlanItemId);
      alert.success(t('itemDeleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return {
    itemsMap,
    cacheManager,
    isLoading: isInitialLoading,
    createLessonPlanItem: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateLessonPlanItem: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteLessonPlanItem: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};

export default useLessonItemsMap;
